import { comActivityTypes } from 'lib/comActivityTypes'

// Fixed-value vocabularies for condition fields — mirrored from the contacts table's filter
// catalog (src/app/table-columns/contacts.tsx), which is the app's source of truth for what each
// enumerable field can hold. A fixed-value field must render as a dropdown, never free text:
// typing "closed lost" against a stored "Closed Lost" is a silent never-match, the exact bug
// family we spent this week killing engine-side (TRK-5421).
//
// NOTE: the table file inlines these same literals; folding both onto one shared module is the
// right end state but touches a high-traffic file other teams live in — flagged, not done here.

export type ValueChoice = { value: string; label: string }

const asChoices = (values: string[]): ValueChoice[] => values.map((v) => ({ value: v, label: v }))

/** Field key → fixed vocabulary. Dynamic vocabularies (pipeline stages, locations) come from the
 *  stores in useConditionValueOptions; this module is the static half. */
export const FIELD_VALUE_OPTIONS: Record<string, ValueChoice[]> = {
  // Contact — matches the contacts-table filter options verbatim.
  leadStatus: asChoices([`Needs Action`, `In Progress`, `Scheduled`, `Closed Won`, `Closed Lost`]),
  lifecycleStage: asChoices([`Lead`, `Qualified Lead`, `New Patient Scheduled`, `Patient`]),
  patientStatus: [
    { value: `Active`, label: `Active` },
    { value: `Inactive`, label: `Inactive` },
    { value: `Archived`, label: `Archived` },
    { value: `NonPatient`, label: `Non-Patient` },
  ],
  appointmentStatus: asChoices([`Unscheduled`, `Scheduled`, `Completed`, `Broken`]),

  // Message — direction/channel as the Message model stores them (verified in the 08-03 live QA).
  messageDirection: [
    { value: `inbound`, label: `Inbound (they texted us)` },
    { value: `outbound`, label: `Outbound (we texted them)` },
  ],
  messageChannel: [
    { value: `sms`, label: `SMS` },
    { value: `email`, label: `Email` },
  ],

  // PMS comm log, as the sync stores it. CHANNEL_UNSPECIFIED is about 41% of live records, so it is a
  // choice ("Other"), not a gap in the list. Direction is absent on about 42%: that is `is empty`.
  commLogChannel: [
    { value: `TEXT`, label: `Text` },
    { value: `PHONE`, label: `Phone` },
    { value: `EMAIL`, label: `Email` },
    { value: `IN_PERSON`, label: `In person` },
    { value: `CHANNEL_UNSPECIFIED`, label: `Other` },
  ],
  sentOrReceived: [
    { value: `1`, label: `Sent by the office` },
    { value: `2`, label: `Received from the patient` },
  ],

  // Call — as stored on dev 2026-10-30.
  direction: [
    { value: `inbound`, label: `Inbound` },
    { value: `outbound`, label: `Outbound` },
  ],
  allPatientOutcomes: asChoices([
    `New Patient`,
    `Appointment Scheduled`,
    `Appointment Not Scheduled`,
    `Appointment Rescheduled`,
    `Appointment Confirmation`,
    `Canceled Appointment`,
    `Callback`,
    `Emergency`,
    `Insurance: State / HMO`,
    `Insurance: Out of Network`,
    `Service Not Offered`,
    `Missed Call`,
    `Voicemail`,
    `Wrong Number`,
    `In Progress`,
    `Other`,
  ]),

  // Treatment plan — stored uppercase.
  state: [
    { value: `OPEN`, label: `Open` },
    { value: `CLOSED`, label: `Closed` },
  ],

  paperworkStatus: [
    { value: `sent`, label: `Sent` },
    { value: `opened`, label: `Opened` },
    { value: `completed`, label: `Completed` },
    { value: `expired`, label: `Expired` },
    { value: `cancelled`, label: `Cancelled` },
  ],
}

/** `status` is the one field key two objects share, with different vocabularies. The right list is
 *  picked by which object's fields are in scope (tasks are the only source of `dueDate`;
 *  appointments the only source of `date`). */
export const TASK_STATUS_OPTIONS: ValueChoice[] = [
  { value: `open`, label: `Open` },
  { value: `closed`, label: `Closed` },
]
/** Activity `type` only: an appointment's type is the practice's own wording and stays free text. */
export const ACTIVITY_TYPE_OPTIONS: ValueChoice[] = asChoices([
  ...new Set([...comActivityTypes, `Change`, `Note`, `Patient`, `Milestone`]),
])
export const APPOINTMENT_STATUS_OPTIONS: ValueChoice[] = asChoices([`Scheduled`, `Completed`, `Broken`])
